import { intl, UUID } from '@beelday/common'
import { useSelector } from 'app-redux'
import { Config } from 'common/config'
import { getListPollingApi } from 'features/pollings/list-polling/list-polling-api'
import { useEffect, useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { ConfiguredArchivedActivity } from './configured-archived-activity'
import { countMine, myAnswers } from './model/answers'
import {
	OpenQuestion,
	OpenQuestionsView,
} from './presentation/open-questions-view'
import { Confirmation } from './presentation/primitives'
import { useConfirmedSend } from './use-confirmed-send'

/**
 * An open-ended list poll on the phone. Other people's answers are not
 * listed - they are on the projector. Whether a question is done is read from
 * the answers in the store, so it survives a reload.
 */
export const ConfiguredOpenQuestions = (): JSX.Element | null => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const me = useAssertedWorkflowUser()
	const poll = useSelector(state => state.listPolling.pollingDefinition)
	const answers = useSelector(state => state.listPolling.answers)
	const questions: OpenQuestion[] = (poll?.questions ?? []).map(question => ({
		id: question.id,
		text: question.text,
		myAnswers: myAnswers(answers[question.id], me.id),
	}))
	/** The question last sent - what the send's status is about. */
	const [sendingId, setSendingId] = useState<UUID>()
	// Only the sent question's own answers confirm it: counted across all of
	// them, a late answer to another question would confirm this one and
	// clear its draft.
	const { status, send } = useConfirmedSend(
		sendingId ? countMine(answers[sendingId], me.id) : 0
	)

	/**
	 * `'auto'` follows the first unanswered question, which is what the
	 * participant wants until they pick one; `null` is "all closed".
	 */
	const [expanded, setExpanded] = useState<UUID | null | 'auto'>('auto')
	const [drafts, setDrafts] = useState<Record<UUID, string>>({})
	// Focus follows the open question only once the participant has acted:
	// on arrival it would throw the keyboard over the list unread.
	const [interacted, setInteracted] = useState(false)

	// The draft goes only once the answer is in the room's state - on a
	// failure it stays, ready to send again.
	useEffect(() => {
		if (status === 'confirmed' && sendingId) {
			setDrafts(current => ({ ...current, [sendingId]: '' }))
			setExpanded('auto')
		}
	}, [status, sendingId])

	if (!poll) {
		return null
	}

	if (poll.doneInPreviousSession) {
		return (
			<ConfiguredArchivedActivity
				toolType="POLLING_OPEN_ENDED_LIST"
				title={poll.name}
			/>
		)
	}

	const multiple = poll.allowForMultipleAnswer
	const allAnswered =
		questions.length > 0 && questions.every(q => q.myAnswers.length > 0)

	if (!multiple && allAnswered && status !== 'sending') {
		return (
			<Confirmation
				title={<intl.Translate>in_person.mobile.sent.all_title</intl.Translate>}
				description={
					<intl.Translate>in_person.mobile.sent.poll</intl.Translate>
				}
				testId="in_person.mobile.poll.sent"
			/>
		)
	}

	const openId =
		expanded === 'auto'
			? questions.find(q => !q.myAnswers.length)?.id
			: expanded ?? undefined

	const sending = status === 'sending' ? sendingId : undefined
	const failed = status === 'failed' ? sendingId : undefined

	return (
		<OpenQuestionsView
			questions={questions}
			multiple={multiple}
			allAnswered={allAnswered}
			openId={openId}
			drafts={drafts}
			sendingId={sending}
			failedId={failed}
			busy={status === 'sending'}
			focusOpen={interacted}
			onToggle={id => {
				setInteracted(true)
				setExpanded(openId === id ? null : id)
			}}
			onDraft={(id, value) =>
				setDrafts(current => ({ ...current, [id]: value }))
			}
			onSend={id => {
				const text = (drafts[id] ?? '').trim()
				if (!text || status === 'sending') {
					return
				}
				setInteracted(true)
				setSendingId(id)
				send(
					() =>
						getListPollingApi(Config.beeldayBackendUrl).sendAnswer(
							roomAddress,
							poll.id,
							id,
							text
						),
					countMine(answers[id], me.id)
				)
			}}
		/>
	)
}

export default ConfiguredOpenQuestions
